import type { V2 } from '../core/math';

/**
 * Generic interaction API. Anything the player can act on with the interact key (E / F / pad Y)
 * registers an Interactable: mission pickups, doors, phone boxes, garages… Vehicles are handled by
 * the player module itself but go through the same prompt pipeline.
 *
 *   const off = ctx.services.get('interactions').register({
 *     id: 'job:phonebox', pos: () => box.pos, radius: 2, prompt: () => 'Answer the phone',
 *     onInteract: () => missions.start('first-job'),
 *   });
 */
export interface Interactable {
  id: string;
  /** World position (called every step — keep it cheap). */
  pos: () => V2;
  /** Activation radius in metres. */
  radius: number;
  /** Prompt text shown next to the key cap. */
  prompt: () => string;
  /** Lower wins when several are in range. Vehicles use 10, missions should use < 10. */
  priority?: number;
  enabled?: () => boolean;
  /** Also available while driving (e.g. drive-in garages). Default false. */
  whileDriving?: boolean;
  onInteract(): void;
}

export interface InteractionPrompt {
  id: string;
  prompt: string;
  pos: V2;
}

export interface InteractionService {
  register(i: Interactable): () => void;
  /** What pressing interact would do right now (null = nothing). */
  readonly current: InteractionPrompt | null;
  /** Disable the built-in prompt label if a UI module renders its own from `current`. */
  setBuiltInPrompt(enabled: boolean): void;
}

declare module '../core/services' {
  interface ServiceMap {
    interactions: InteractionService;
  }
}

/** Pick the best interactable for a player at `p`. Pure — used by the player system and tests. */
export function pickInteractable(list: readonly Interactable[], p: V2, driving: boolean): Interactable | null {
  let best: Interactable | null = null;
  let bestScore = Infinity;
  for (const it of list) {
    if (driving && !it.whileDriving) continue;
    if (it.enabled && !it.enabled()) continue;
    const q = it.pos();
    const d = Math.hypot(q.x - p.x, q.z - p.z);
    if (d > it.radius) continue;
    const score = (it.priority ?? 5) * 1000 + d;
    if (score < bestScore) {
      bestScore = score;
      best = it;
    }
  }
  return best;
}
